import {
  HomeForManagers,
  HomeForManagers_exploreCategory_subCategories_content,
} from './__generated__/HomeForManagers';

export type ManagerContentItem =
  HomeForManagers_exploreCategory_subCategories_content;

/**
 * Courses first, then tools. Only Series and Tool reach here (see
 * `isRenderableContent`), so a non-Series is always a Tool.
 */
const contentRank = (item: ManagerContentItem) =>
  item.__typename === 'Series' ? 0 : 1;

/**
 * The query only selects Series and Tool inline fragments, but the field is a
 * union: any other member the backend adds later comes back as a bare
 * `{ __typename }` (no fragment matched) and would otherwise flow on to
 * `GenericExploreContent`, which treats every non-Tool as a Series and crashes
 * on the missing course fields. Drop anything that isn't Series or Tool.
 */
const isRenderableContent = (item: ManagerContentItem): boolean =>
  item.__typename === 'Series' || item.__typename === 'Tool';

/**
 * Flattens the manager category's subcategories into one courses-first list.
 *
 * The managers page gives each subcategory its own row; the homepage has one,
 * so the grouping is flattened. A stable sort on type keeps the API's order
 * within each group.
 *
 * Not sliced to a tile count — `GenericExploreContent` already truncates, and
 * duplicating that would put the responsive rule in two places.
 */
export const selectManagerContent = (
  data?: HomeForManagers | null,
): ManagerContentItem[] => {
  const items = (data?.exploreCategory?.subCategories ?? []).flatMap(
    subCategory =>
      (subCategory?.content ?? []).filter(
        (item): item is ManagerContentItem =>
          item !== null && isRenderableContent(item),
      ),
  );

  return [...items].sort((a, b) => contentRank(a) - contentRank(b));
};
